import { useCallback, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { atom, useRecoilState } from 'recoil';
import { InfoBannerMap } from '../components/pm/models';
import {
  useGetBannerStatus,
  useUpdateBannerStatus,
} from '../components/pm/services';

export const useQueryParams = () => {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
};

export const useGetQueryParam = (param: string) => {
  const queryParams = useQueryParams();
  return useMemo(() => {
    return queryParams.get(param);
  }, [param, queryParams]);
};

export const useGetAllQueryParams = (param: string) => {
  const queryParams = useQueryParams();
  return useMemo(() => {
    return queryParams.getAll(param);
  }, [param, queryParams]);
};

export const useShowInfoBanner = (key: InfoBannerMap) => {
  const { data: infoBannerData } = useGetBannerStatus();

  return infoBannerData?.[key] || false;
};

export type AppFeatureBanner = Record<InfoBannerMap, boolean>;

export const DefaultSetupGuideState: AppFeatureBanner = {
  [InfoBannerMap.Proposal]: true,
  [InfoBannerMap.Client]: true,
  [InfoBannerMap.Deal]: true,
  [InfoBannerMap.Contact]: true,
  [InfoBannerMap.Work]: true,
  [InfoBannerMap.Services]: true,
  [InfoBannerMap.Payments]: true,
  [InfoBannerMap.Settings_Branding]: true,
  [InfoBannerMap.Settings_Integrations]: true,
  [InfoBannerMap.Settings_Reminders]: true,
  [InfoBannerMap.Settings_Workflow]: true,
  [InfoBannerMap.Time]: true,
  [InfoBannerMap.Invoice]: true,
};

export const BannerState = atom<AppFeatureBanner>({
  key: 'BannerState',
  default: DefaultSetupGuideState,
});

export const useBannerState = (banner: InfoBannerMap) => {
  const [bannerState, setBannerState] =
    useRecoilState<AppFeatureBanner>(BannerState);
  const { data: infoBannerData } = useGetBannerStatus();
  const { mutate: updateInfoBanner } = useUpdateBannerStatus();

  const handleBannerState = useCallback(
    (state: boolean) => {
      const newBannerState = {
        ...bannerState,
        [banner]: state,
      };
      setBannerState(newBannerState);
      updateInfoBanner(newBannerState);
    },
    [banner, bannerState, setBannerState, updateInfoBanner],
  );

  useEffect(() => {
    if (infoBannerData)
      setBannerState((prev) => ({
        ...prev,
        ...infoBannerData,
      }));
  }, [infoBannerData, setBannerState]);

  return {
    state: bannerState[banner],
    handleBannerState,
  };
};

export const SetupGuideBannerState = atom<boolean>({
  key: 'SetupGuideBannerState',
  default: false,
});

export const useSetupGuideBannerState = () => {
  const [isSetupGuideOpen, setIsSetupGuideOpen] = useRecoilState<boolean>(
    SetupGuideBannerState,
  );

  return {
    isSetupGuideOpen,
    setIsSetupGuideOpen,
  };
};
